import { useState, type FormEvent } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { admin } from '@repletoryreach/sdk-api';
import { Anchor, Button, Card, Group, SimpleGrid, Stack, Text, Badge } from '@repletoryreach/sdk-ui';
import { Select, Switch, TextInput } from '@repletoryreach/sdk-ui/inputs';
import { Alert, DataTable, StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifyError, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { TwoLineCell } from '../../../../components/TwoLineCell';
import { useCreateMetaNewsSource, useDeleteMetaNewsSource, useRefreshMetaNews, useUpdateMetaNewsSource } from '../../../../data/useMetaNews';
import { formatDateTime, formatInteger, formatRelative } from '../../../../lib/format';
import type { Indexed } from '../../../../lib/types';
import { useT } from '../../../../i18n/I18nProvider';

const KINDS: admin.MetaNewsSourceKind[] = ['page', 'atom', 'rss'];
const FORM_COLS = { base: 1, sm: 3 };

/**
 * The places that are checked: turn one off, see why its last check failed in
 * plain words, check it on its own, add a new one or delete one.
 */
export function SourcesCard({ query }: { query: UseQueryResult<{ data: admin.MetaNewsSource[] }> }) {
  const t = useT();
  const [adding, setAdding] = useState(false);
  const update = useUpdateMetaNewsSource();
  const del = useDeleteMetaNewsSource();
  const refresh = useRefreshMetaNews();
  const sources = query.data?.data ?? [];
  const title = t('billing.metaUpdates.sources.title');
  const fail = (e: unknown) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), title);

  function setEnabled(source: admin.MetaNewsSource, enabled: boolean) {
    update.mutate({ id: source.id, enabled }, {
      onSuccess: () => notifySuccess(t('billing.metaUpdates.sources.updated', { name: source.name }), title),
      onError: fail
    });
  }

  function checkOne(source: admin.MetaNewsSource) {
    refresh.mutate(source.id, {
      onSuccess: (res) => {
        const r = res.data[0];
        if (!r) return;
        if (r.ok) notifySuccess(t('billing.metaUpdates.sources.checked', { name: r.name, count: formatInteger(r.new_items) }), title);
        else notifyError(t('billing.metaUpdates.sources.checkFailed', { name: r.name, error: r.error ?? '' }), title);
      },
      onError: fail
    });
  }

  function remove(source: admin.MetaNewsSource) {
    openConfirmModal({
      title: t('billing.metaUpdates.sources.confirm.deleteTitle', { name: source.name }),
      children: <Text role="bodySmall">{t('billing.metaUpdates.sources.confirm.deleteBody', { count: formatInteger(source.item_count) })}</Text>,
      labels: { confirm: t('action.delete'), cancel: t('action.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => del.mutate(source.id, {
        onSuccess: () => notifySuccess(t('billing.metaUpdates.sources.deleted', { name: source.name }), title),
        onError: fail
      })
    });
  }

  return (
    <Card withBorder title={title}>
      <Stack gap="md">
        <Group gap="sm" justify="space-between" wrap="wrap">
          <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('billing.metaUpdates.sources.description')}</Text>
          {!adding && <Button size="xs" onClick={() => setAdding(true)}>{t('billing.metaUpdates.sources.add')}</Button>}
        </Group>

        {adding && <AddSourceForm onDone={() => setAdding(false)} />}

        {query.error ? (
          <Alert color="error">
            <Stack gap="sm">
              <Text role="bodySmall">{t('billing.metaUpdates.sources.loadError')}</Text>
              <Group><Button size="xs" variant="outline" onClick={() => void query.refetch()}>{t('action.retry')}</Button></Group>
            </Stack>
          </Alert>
        ) : (
          <DataTable<Indexed<admin.MetaNewsSource>>
            records={sources as Indexed<admin.MetaNewsSource>[]}
            loading={query.isLoading}
            idAccessor="id"
            emptyState={t('billing.metaUpdates.sources.empty')}
            columns={[
              {
                accessor: 'name',
                title: t('billing.metaUpdates.sources.columns.source'),
                render: (r) => (
                  <TwoLineCell
                    primary={r.name}
                    secondary={<Anchor href={r.url} target="_blank" className="rr-meta-updates__url">{r.url}</Anchor>}
                    title={r.name}
                  />
                )
              },
              {
                accessor: 'kind',
                title: t('billing.metaUpdates.sources.columns.kind'),
                render: (r) => <Badge size="xs" variant="light" color="gray">{t(`billing.metaUpdates.kind.${r.kind}`)}</Badge>
              },
              {
                accessor: 'enabled',
                title: t('billing.metaUpdates.sources.columns.enabled'),
                render: (r) => (
                  <Switch
                    size="xs"
                    checked={r.enabled}
                    aria-label={t('billing.metaUpdates.sources.enabledLabel', { name: r.name })}
                    disabled={update.isPending && update.variables?.id === r.id}
                    onChange={(v) => setEnabled(r, v)}
                  />
                )
              },
              {
                accessor: 'last_checked_at',
                title: t('billing.metaUpdates.sources.columns.status'),
                render: (r) => (
                  <Stack gap="xs">
                    <Group gap="xs" wrap="wrap">
                      {!r.enabled
                        ? <StatusBadge status="inactive">{t('billing.metaUpdates.sources.status.off')}</StatusBadge>
                        : r.last_error
                          ? <StatusBadge status="down">{t('billing.metaUpdates.sources.status.failing')}</StatusBadge>
                          : r.last_success_at
                            ? <StatusBadge status="healthy">{t('billing.metaUpdates.sources.status.working')}</StatusBadge>
                            : <StatusBadge status="pending">{t('billing.metaUpdates.sources.status.notChecked')}</StatusBadge>}
                      {r.last_checked_at && (
                        <span title={formatDateTime(r.last_checked_at)}>
                          <Text role="caption" c="var(--rr-color-text-muted)">{t('billing.metaUpdates.sources.checkedWhen', { when: formatRelative(r.last_checked_at) })}</Text>
                        </span>
                      )}
                    </Group>
                    {r.last_error && <Text role="caption" c="var(--rr-color-error)" className="rr-meta-updates__error">{r.last_error}</Text>}
                  </Stack>
                )
              },
              {
                accessor: 'item_count',
                title: t('billing.metaUpdates.sources.columns.items'),
                render: (r) => (
                  <Text role="bodySmall">
                    {t('billing.metaUpdates.sources.itemCount', { count: formatInteger(r.item_count), unread: formatInteger(r.unread_count) })}
                  </Text>
                )
              },
              {
                accessor: 'actions',
                title: t('common.actions'),
                textAlign: 'right',
                render: (r) => (
                  <Group gap="xs" justify="flex-end" wrap="nowrap">
                    <Button size="xs" variant="outline" disabled={!r.enabled || (refresh.isPending && refresh.variables === r.id)} onClick={() => checkOne(r)}>
                      {refresh.isPending && refresh.variables === r.id ? t('billing.metaUpdates.action.checking') : t('billing.metaUpdates.sources.checkNow')}
                    </Button>
                    <Button size="xs" variant="subtle" color="red" onClick={() => remove(r)}>{t('action.delete')}</Button>
                  </Group>
                )
              }
            ]}
          />
        )}
      </Stack>
    </Card>
  );
}

function AddSourceForm({ onDone }: { onDone: () => void }) {
  const t = useT();
  const create = useCreateMetaNewsSource();
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [kind, setKind] = useState<admin.MetaNewsSourceKind>('page');
  const [error, setError] = useState<string | null>(null);

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    create.mutate({ name: name.trim(), url: url.trim(), kind }, {
      onSuccess: (res) => {
        notifySuccess(t('billing.metaUpdates.sources.added', { name: res.data.name }), t('billing.metaUpdates.sources.title'));
        onDone();
      },
      onError: (err) => setError(err instanceof Error ? err.message : t('common.unknownError'))
    });
  }

  return (
    <form onSubmit={submit} className="rr-meta-updates__form">
      <Stack gap="md">
        <SimpleGrid cols={FORM_COLS} spacing="md">
          <TextInput
            label={t('billing.metaUpdates.form.name')}
            description={t('billing.metaUpdates.form.nameHint')}
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            required
          />
          <TextInput
            type="url"
            label={t('billing.metaUpdates.form.url')}
            description={t('billing.metaUpdates.form.urlHint')}
            placeholder={t('billing.metaUpdates.form.urlPlaceholder')}
            value={url}
            onChange={(e) => setUrl(e.currentTarget.value)}
            required
          />
          <Select
            label={t('billing.metaUpdates.form.kind')}
            description={t('billing.metaUpdates.form.kindHint')}
            value={kind}
            onChange={(e) => setKind(e.currentTarget.value as admin.MetaNewsSourceKind)}
            data={KINDS.map((k) => ({ value: k, label: t(`billing.metaUpdates.kind.${k}`) }))}
          />
        </SimpleGrid>
        {error && <Alert color="error">{error}</Alert>}
        <Group gap="sm">
          <Button size="xs" type="submit" disabled={create.isPending || !name.trim() || !url.trim()}>{t('billing.metaUpdates.form.save')}</Button>
          <Button size="xs" variant="subtle" onClick={onDone}>{t('action.cancel')}</Button>
        </Group>
      </Stack>
    </form>
  );
}
